// Rough-timing helpers for Event Ideas.
//
// No Vue/Nuxt imports — auto-imported by Nuxt from `app/utils/`. Pure, so the
// whole tagged union can be unit-tested without mounting anything.
//
// Two hard invariants, both mirrored from the backend `IdeaTimingValidator`:
//
//   1. `exact` REQUIRES a non-empty `starts_at`; `ends_at` is optional but must
//      not precede it.
//   2. `month` / `season` / `year` / `tba` MUST carry literal `null` for BOTH
//      `starts_at` and `ends_at`. Fabricating a datetime for a coarse kind is a
//      422 — never "helpfully" derive one.
//
// Unlike Almanac event submissions, ideas have NO future-date requirement: an
// idea is a pitch, and pitching a window that has already slipped is the
// author's business, not a validation failure.

import type { IdeaTiming, IdeaTimingKind } from '@@/types/api';

export const IDEA_TIMING_KINDS = [
  'exact',
  'month',
  'season',
  'year',
  'tba',
] as const;

export const IDEA_SEASONS = ['Spring', 'Summer', 'Fall', 'Winter'] as const;

export type IdeaSeason = (typeof IDEA_SEASONS)[number];

/** Maximum length of the minted `display` string (backend: `max:255`). */
export const IDEA_TIMING_DISPLAY_MAX = 255;

/**
 * Raw form input. Every field is a string because Vuetify text/select inputs
 * bind strings — the store keeps flat string refs and hands them here verbatim.
 * Only the fields relevant to `kind` are read; the rest are ignored.
 */
export interface IdeaTimingInput {
  kind: IdeaTimingKind;
  /** `exact` only — `Y-m-d`. */
  startDate?: string;
  /** `exact` only — `Y-m-d`. Blank means "single day". */
  endDate?: string;
  /** `month` only — `1`–`12`. */
  month?: string;
  /** `season` only — one of `IDEA_SEASONS`. */
  season?: string;
  /** `month` / `season` / `year` — four-digit year. */
  year?: string;
}

// ---------------------------------------------------------------------------
// Display formatting (ported from the submission funnel's `formatDayRange`)
// ---------------------------------------------------------------------------

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function monthLabel(month: number): string {
  return MONTH_NAMES[month - 1] ?? '';
}

function parseYmd(value: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
}

// en-dash (U+2013) separated ranges, collapsing shared month/year:
//   same day            → "June 12, 2027"
//   same month + year   → "June 12–14, 2027"
//   same year           → "May 30 – June 2, 2027"
//   different year      → "December 30, 2027 – January 2, 2028"
function formatDayRange(startDate: string, endDate: string): string {
  const start = parseYmd(startDate);
  const end = parseYmd(endDate);
  if (!start || !end) return '';

  const startMonth = monthLabel(start.m);
  const endMonth = monthLabel(end.m);

  if (start.y === end.y && start.m === end.m && start.d === end.d) {
    return `${startMonth} ${start.d}, ${start.y}`;
  }
  if (start.y === end.y && start.m === end.m) {
    return `${startMonth} ${start.d}–${end.d}, ${start.y}`;
  }
  if (start.y === end.y) {
    return `${startMonth} ${start.d} – ${endMonth} ${end.d}, ${start.y}`;
  }
  return `${startMonth} ${start.d}, ${start.y} – ${endMonth} ${end.d}, ${end.y}`;
}

function isFourDigitYear(value: string): boolean {
  return /^\d{4}$/.test(value);
}

/**
 * The human label for a timing input. Returns `''` when the input is too
 * incomplete to label — `validateIdeaTiming` is what turns that into an error.
 */
export function ideaTimingDisplay(input: IdeaTimingInput): string {
  const year = (input.year ?? '').trim();

  switch (input.kind) {
    case 'exact': {
      const startDate = (input.startDate ?? '').trim();
      if (startDate === '') return '';
      const endDate = (input.endDate ?? '').trim() || startDate;
      return formatDayRange(startDate, endDate);
    }
    case 'month': {
      const month = Number((input.month ?? '').trim());
      if (!Number.isInteger(month) || month < 1 || month > 12) return '';
      if (!isFourDigitYear(year)) return '';
      return `${monthLabel(month)} ${year}`;
    }
    case 'season': {
      const season = (input.season ?? '').trim();
      if (!(IDEA_SEASONS as readonly string[]).includes(season)) return '';
      if (!isFourDigitYear(year)) return '';
      return `${season} ${year}`;
    }
    case 'year':
      return isFourDigitYear(year) ? year : '';
    case 'tba':
      return 'Dates TBA';
    default:
      return '';
  }
}

// ---------------------------------------------------------------------------
// Payload construction
// ---------------------------------------------------------------------------

/**
 * Build the `timing` sub-object for a create/update payload.
 *
 * `exact` widens the chosen `Y-m-d` day(s) into a UTC day window — ideas carry
 * no timezone, and the backend only needs an ordered pair. Every coarse kind
 * emits literal `null` for both datetimes, which is a backend requirement, not
 * a nicety.
 */
export function buildIdeaTiming(input: IdeaTimingInput): IdeaTiming {
  const display = ideaTimingDisplay(input);

  if (input.kind !== 'exact') {
    return {
      kind: input.kind,
      display,
      starts_at: null,
      ends_at: null,
    };
  }

  const startDate = (input.startDate ?? '').trim();
  const endDate = (input.endDate ?? '').trim();

  return {
    kind: 'exact',
    display,
    starts_at: startDate === '' ? null : `${startDate}T00:00:00Z`,
    ends_at: endDate === '' ? null : `${endDate}T23:59:59Z`,
  };
}

// ---------------------------------------------------------------------------
// Validation (mirrors the backend `IdeaTimingValidator`)
// ---------------------------------------------------------------------------

function isNonEmptyString(value: unknown): boolean {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * Validate an already-built `IdeaTiming`. Returns a map keyed by the BACKEND
 * field paths (`timing.kind`, `timing.starts_at`, …) so the result can be
 * merged straight into the same `serverErrors` bag a 422 populates. An empty
 * object means valid.
 */
export function validateIdeaTiming(timing: IdeaTiming): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!(IDEA_TIMING_KINDS as readonly string[]).includes(timing.kind)) {
    errors['timing.kind'] = 'Choose when this might happen.';
    return errors;
  }

  if (!isNonEmptyString(timing.display)) {
    errors['timing.display'] = 'Finish choosing a date or timeframe.';
  } else if (timing.display.length > IDEA_TIMING_DISPLAY_MAX) {
    errors['timing.display'] =
      `The timing label must be ${IDEA_TIMING_DISPLAY_MAX} characters or fewer.`;
  }

  if (timing.kind === 'exact') {
    if (!isNonEmptyString(timing.starts_at)) {
      errors['timing.starts_at'] = 'A start date is required for exact dates.';
      return errors;
    }

    const start = new Date(timing.starts_at as string).getTime();
    if (Number.isNaN(start)) {
      errors['timing.starts_at'] = 'The start date could not be read.';
      return errors;
    }

    if (timing.ends_at !== null && timing.ends_at !== undefined) {
      const end = new Date(timing.ends_at).getTime();
      if (Number.isNaN(end)) {
        errors['timing.ends_at'] = 'The end date could not be read.';
      } else if (end < start) {
        errors['timing.ends_at'] = 'The end date must not be before the start.';
      }
    }

    return errors;
  }

  // Coarse kinds carry NO datetimes — a fabricated one is a 422 server-side.
  if (timing.starts_at !== null) {
    errors['timing.starts_at'] =
      'This timeframe must not carry a specific start date.';
  }
  if (timing.ends_at !== null) {
    errors['timing.ends_at'] =
      'This timeframe must not carry a specific end date.';
  }

  return errors;
}
